import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiSearch, FiUsers, FiBriefcase, FiMessageSquare, FiBookOpen, FiUser, FiX } from "react-icons/fi";
import { globalSearch } from "../../api/search";
import { Spinner } from "../ui";

const CATEGORIES = [
  { key: "communities", label: "Communities", icon: FiUsers, path: (r) => `/communities/${r.id}` },
  { key: "jobs", label: "Jobs", icon: FiBriefcase, path: (r) => `/jobs?highlight=${r.id}` },
  { key: "users", label: "People", icon: FiUser, path: (r) => `/profile/${r.id}` },
  { key: "posts", label: "Posts", icon: FiMessageSquare, path: (r) => `/dashboard?post=${r.id}` },
  { key: "courses", label: "Learning", icon: FiBookOpen, path: (r) => `/courses/${r.id}` },
];

function resultLabel(category, r) {
  if (category === "users") return `${r.name} ${r.lastName || ""}`.trim();
  if (category === "jobs") return `${r.title} — ${r.company}`;
  if (category === "posts") return r.content?.slice(0, 60) || "Untitled post";
  return r.title || r.name;
}

export default function GlobalSearch() {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const debounceRef = useRef(null);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  const latestQueryRef = useRef("");

  // Debounced so every keystroke doesn't fire its own request — 300ms is
  // short enough to feel instant, long enough to skip the in-between
  // states while someone's still typing.
  useEffect(() => {
    if (query.trim().length < 2) {
      setResults(null);
      return;
    }
    const trimmedQuery = query.trim();
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      latestQueryRef.current = trimmedQuery;
      setLoading(true);
      globalSearch(trimmedQuery)
        .then((res) => {
          // Two requests can be in flight at once if someone pauses mid-word
          // (firing a request), then keeps typing before it resolves (firing
          // a second). A ref (not the closed-over query state, which is
          // frozen at the value it had when this effect ran) always reflects
          // the truly latest query, so a slower earlier response is
          // correctly discarded instead of overwriting newer, correct
          // results with stale ones.
          if (trimmedQuery !== latestQueryRef.current) return;
          setResults(res.data?.results || null);
        })
        .catch(() => setResults(null))
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    const handleEscape = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  const handleSelect = (category, result) => {
    const config = CATEGORIES.find((c) => c.key === category);
    navigate(config.path(result));
    setOpen(false);
    setQuery("");
    setResults(null);
  };

  const hasAnyResults = results && Object.values(results).some((list) => list.length > 0);

  return (
    <div ref={containerRef} className="relative w-full max-w-sm">
      <div className="relative">
        <FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Search people, jobs, communities..."
          className="h-10 w-full rounded-lg border border-slate-200 bg-slate-50 pl-9 pr-8 text-sm placeholder:text-slate-400 focus:border-primary-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-100"
        />
        {query && (
          <button
            onClick={() => { setQuery(""); setResults(null); }}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-500"
            aria-label="Clear search"
          >
            <FiX className="h-4 w-4" />
          </button>
        )}
      </div>

      {open && query.trim().length >= 2 && (
        <div className="absolute left-0 right-0 top-full z-30 mt-2 max-h-[70vh] overflow-y-auto rounded-xl border border-slate-100 bg-white shadow-popover">
          {loading ? (
            <div className="flex justify-center py-8"><Spinner className="h-5 w-5" /></div>
          ) : !hasAnyResults ? (
            <p className="px-4 py-6 text-center text-sm text-slate-400">No results for "{query}"</p>
          ) : (
            CATEGORIES.map(({ key, label, icon: Icon }) => {
              const list = results?.[key] || [];
              if (list.length === 0) return null;
              return (
                <div key={key} className="border-b border-slate-50 py-2 last:border-none">
                  <p className="px-4 pb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</p>
                  {list.map((r) => (
                    <button
                      key={r.id}
                      onClick={() => handleSelect(key, r)}
                      className="flex w-full items-center gap-3 px-4 py-2 text-left hover:bg-slate-50"
                    >
                      <Icon className="h-4 w-4 shrink-0 text-primary-500" />
                      <span className="truncate text-sm text-slate-700">{resultLabel(key, r)}</span>
                    </button>
                  ))}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
